// W2Cook Billing Dashboard — Live API polling hook
//
// Connectors flagged corsOk are called directly from the browser.
// Everything else is relayed through the Cloud Run /proxy route, because
// those vendors do not send CORS headers and the browser blocks them.

import { useState, useCallback, useEffect, useRef } from "react";
import type { ConnectorTemplate, LiveResult } from "../connectors/templates";
import { auth } from "../auth/firebase";

const POLL_INTERVAL_MS = 120_000;
const PROXY_URL = import.meta.env.VITE_PROXY_URL ?? "";

export type LiveDataMap = Record<string, LiveResult>;
export type FetchingMap = Record<string, boolean>;

async function callVendor(
  tpl: ConnectorTemplate,
  cfg: Record<string, string>,
): Promise<{ status: number; data: unknown }> {
  const url     = tpl.buildEndpoint(cfg);
  const headers = tpl.buildHeaders(cfg);

  // Direct — vendor supports CORS.
  if (tpl.corsOk) {
    const res  = await fetch(url, { headers });
    const data = await res.json().catch(() => ({}));
    return { status: res.status, data };
  }

  // Relayed — requires the proxy to be configured.
  if (!PROXY_URL) {
    return {
      status: 0,
      data: { error: "This connector needs the API proxy. Set VITE_PROXY_URL." },
    };
  }

  const idToken = auth ? await auth.currentUser?.getIdToken() : undefined;
  if (!idToken) {
    return { status: 401, data: { error: "Not signed in — cannot authenticate to proxy." } };
  }

  const res = await fetch(`${PROXY_URL}/proxy`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ url, headers, method: "GET" }),
  });
  const data = await res.json().catch(() => ({}));
  return { status: res.status, data };
}

export function useLiveData(
  templates: ConnectorTemplate[],
  configs: Record<string, Record<string, string>>,
) {
  const [liveData,   setLiveData  ] = useState<LiveDataMap>({});
  const [fetching,   setFetching  ] = useState<FetchingMap>({});
  const [lastPolled, setLastPolled] = useState<Date | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const configsRef   = useRef(configs);
  const templatesRef = useRef(templates);
  useEffect(() => { configsRef.current = configs; },     [configs]);
  useEffect(() => { templatesRef.current = templates; }, [templates]);

  const fetchOne = useCallback(async (templateId: string) => {
    const tpl = templatesRef.current.find((t) => t.id === templateId);
    const cfg = configsRef.current[templateId];
    if (!tpl || !cfg) return;

    setFetching((p) => ({ ...p, [templateId]: true }));
    try {
      const { status, data } = await callVendor(tpl, cfg);
      const ok = status >= 200 && status < 300;

      if (!ok) {
        const msg = (data as any)?.error ?? (data as any)?.message ?? `HTTP ${status}`;
        setLiveData((p) => ({
          ...p,
          [templateId]: { ok: false, amount: null, note: String(msg), fetchedAt: new Date().toISOString() },
        }));
        return;
      }

      const parsed = tpl.parseResult(data, cfg);
      setLiveData((p) => ({
        ...p,
        [templateId]: { ...parsed, ok: parsed.ok, fetchedAt: new Date().toISOString() },
      }));
    } catch (e: any) {
      setLiveData((p) => ({
        ...p,
        [templateId]: {
          ok: false, amount: null,
          note: `Error: ${e?.message ?? "unknown"}`,
          fetchedAt: new Date().toISOString(),
        },
      }));
    } finally {
      setFetching((p) => ({ ...p, [templateId]: false }));
    }
  }, []);

  const fetchAll = useCallback(async () => {
    const configured = Object.keys(configsRef.current);
    await Promise.allSettled(configured.map(fetchOne));
    setLastPolled(new Date());
  }, [fetchOne]);

  useEffect(() => {
    fetchAll();
    timerRef.current = setInterval(fetchAll, POLL_INTERVAL_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fetchAll]);

  return { liveData, fetching, lastPolled, fetchOne, fetchAll };
}
